<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  reactive,
  ref,
  unref,
  watch,
} from "vue";
import { convertClassFilterCriteria } from "../../utils";
import { PencilIcon } from "@heroicons/vue/24/outline";
import TrashCan from "@/components/shared/icons/TrashCan.vue";
import { useGetClasses } from "@/components/program-manager/sessions/composable";
import { miniTableColumns } from "@/components/program-manager/sessions/components/Academics/constants";
import { getFilterLabel } from "@/components/program-manager/sessions/utils";
import Pagination from "@/components/shared/Pagination.vue";
import { FlexRender } from "@tanstack/vue-table";
import Spinner from "@/components/helpers/Spinner.vue";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useVueTable,
} from "@tanstack/vue-table";

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["editFilters", "clearFilters"]);

const onClear = () => {
  emit("clearFilters", true);
};
const onEdit = () => {
  emit("editFilters", true);
};

const classesParams = ref(
  convertClassFilterCriteria(props.filters, props.terms, props.hostInstitution)
);

const {
  isLoading: classesDataLoading,
  execute: fetchClassesData,
  state: classesData,
  isReady: classesIsReady,
} = useGetClasses(
  { immediate: true, throwError: false, resetOnExecute: true },
  { ...classesParams.value, limit: 10, skip: 0 }
);

const pagination = reactive({
  pageIndex: 1,
  pageSize: 10,
});

const columnHelper = createColumnHelper();
const columns = miniTableColumns.map(
  ({ header, id, size, type, addedClasses, getColumnDisplayValue }) => {
    return columnHelper.accessor(getColumnDisplayValue, {
      id,
      header,
      size,
      type,
      addedClasses,
    });
  }
);

const table = useVueTable({
  get data() {
    return classesData?.value?.data || [];
  },
  enableColumnResizing: true,
  columns: columns,
  columnResizeMode: "onChange",
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  manualPagination: true,
});

const totalRecords = computed(() =>
  classesData?.value?.count && classesData?.value?.count !== -1
    ? classesData.value.count
    : 0
);

const changePage = (pageIndex) => {
  pagination.pageIndex = pageIndex;
  fetchClassesData(0, {
    ...unref(classesParams),
    limit: pagination.pageSize,
    skip: pagination.pageSize * (pagination.pageIndex - 1),
  });
};

const defaultClass = ref("");
const setClassesDefault = (id) => {
  defaultClass.value = id;
};

watch(
  [() => props.filters, () => props.terms, () => props.hostInstitution],
  ([newFilters, newTerms, newHostInstitution]) => {
    classesParams.value = convertClassFilterCriteria(
      newFilters,
      newTerms,
      newHostInstitution
    );
    fetchClassesData(0, {
      ...classesParams.value,
      limit: pagination.pageSize,
      skip: pagination.pageSize * (pagination.pageIndex - 1),
      cancel: true,
    });
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col col-span-2">
    <div class="block text-xs font-semibold text-gray-700">From Library</div>
    <div
      class="grid grid-cols-10 gap-x-2 gap-y-4 items-center bg-white border-gray-300 rounded-md w-full p-4 mt-2"
    >
      <div class="min-h-8 flex flex-wrap items-center col-span-9">
        <template v-for="filter in filters">
          <template v-if="Array.isArray(filter)">
            <div
              v-for="item in filter"
              :key="item.id"
              class="inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100"
            >
              {{ getFilterLabel(item) }}
            </div>
          </template>
          <template v-else-if="filter">
            <div
              class="capitalize inline-flex items-center rounded-full bg-blue-400 mb-1 mr-2 py-0.5 px-2.5 text-sm font-medium text-purple-100"
            >
              {{ filter }}
            </div>
          </template>
        </template>
      </div>
      <div class="flex w-12 justify-center align-center ml-auto">
        <pencil-icon class="w-[18px] mr-3 cursor-pointer" @click="onEdit" />
        <TrashCan class="w-[18px] m-auto cursor-pointer" @click="onClear" />
      </div>
      <div class="col-span-10">
        <table class="block">
          <thead>
            <tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
            >
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :colSpan="header.colSpan"
                :style="{ width: `${header.getSize()}px` }"
                class="py-4 text-left text-sm items-center text-gray-900"
              >
                <div
                  class="flex items-center"
                  :class="header.column.columnDef.addedClasses"
                >
                  <FlexRender
                    v-if="!header.isPlaceholder"
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr
              v-for="row in table.getRowModel().rows"
              :key="row.id"
              class="hover:bg-blue-350"
            >
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.column.id"
                :style="{ width: `${cell.column.getSize()}px` }"
                class="py-2 text-sm capitalize"
                :class="cell.column.columnDef.addedClasses"
              >
                <div v-if="cell.column.columnDef.type === 'default_checkbox'">
                  <input
                    type="radio"
                    :value="defaultClass === cell.row.original.id"
                    :checked="defaultClass === cell.row.original.id"
                    class="h-4 w-4 border-gray-300 form-radio"
                    @input="() => setClassesDefault(cell.row.original.id)"
                  />
                </div>
                <FlexRender
                  v-else
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </td>
            </tr>
          </tbody>
          <Spinner
            v-show="classesDataLoading || !classesIsReady"
            outer-classes="flex items-center justify-center bg-white h-full relative"
          />
        </table>

        <Pagination
          outer-classes="flex items-center justify-between border-t border-gray-200 pt-4"
          :page="pagination.pageIndex"
          :total-of-records="totalRecords"
          :records-per-page="pagination.pageSize"
          @changePage="changePage"
        />
      </div>
    </div>
  </div>
</template>
